import { api } from '@/lib/api';
import { useSessao } from '@/lib/auth';
import { clientIds } from '@/lib/constants';
import { useTranslation } from '@/lib/i18n';
import { isPageEnabled } from '@/lib/migration';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import useTextoOpcaoBioWeb from '../../-components/opcoes/bioweb/useTextoOpcaoBioWeb';
import type { ClienteSolicitacao } from '../../-components/opcoes/cliente';
import PaginaCliente, {
  type PaginaClienteOpcao,
} from '../../-components/pagina-cliente/pagina-cliente';
import PaginaClienteIsolada from '../../-components/pagina-cliente/pagina-cliente-isolada';
import { MAXIMO_CODIGO, parseIdCliente } from '../../-utils/parametros-cliente';
import ServicoWebPageComponent from './-component';

export const Route = createFileRoute(
  '/__authenticated/comercial/produto/clientes/web/$codigo',
)({
  params: {
    parse: params => ({ codigo: parseIdCliente(params.codigo, MAXIMO_CODIGO) }),
    stringify: params => ({ codigo: String(params.codigo) }),
  },
  context: ({ params }) => ({
    servicoQueryOptions: api.queryOptions(
      'get',
      '/clientes/servicos-web/{codigo}',
      {
        params: { path: { codigo: params.codigo } },
      },
      { staleTime: 1000 * 60 },
    ),
    cliente: {
      tipoCliente: 'ServicoWeb',
      codigo: params.codigo,
    } satisfies ClienteSolicitacao as ClienteSolicitacao,
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(context.servicoQueryOptions);
  },
  component: RouteComponent,
});

function RouteComponent() {
  // Com `clientes` desativada, o guard de clientes só deixa chegar aqui a rota de
  // alterar dados, que abre sem as outras opções do cliente.
  if (!isPageEnabled('clientes')) {
    return <PaginaServicoWebIsolada />;
  }

  return <PaginaServicoWeb />;
}

function PaginaServicoWebIsolada() {
  const { servicoQueryOptions } = Route.useRouteContext();

  const { isPending, error } = useQuery(servicoQueryOptions);

  return (
    <PaginaClienteIsolada
      carregando={isPending}
      erro={error}
      opcaoAberta={<Outlet />}
    >
      <ServicoWebPageComponent />
    </PaginaClienteIsolada>
  );
}

function PaginaServicoWeb() {
  const { t } = useTranslation('private');

  const { servicoQueryOptions, cliente } = Route.useRouteContext();
  const search = Route.useSearch();

  const { data: servicoWeb, isPending, error } = useQuery(servicoQueryOptions);
  const { data: usuario } = useSessao();

  const ehAdmin = usuario?.tipo === 'Admin';

  const cancelado = !!servicoWeb?.contratoCancelado;

  const textoBioWeb = useTextoOpcaoBioWeb(cliente, {
    permiteAtivar: !cancelado,
  });

  const opcoes: PaginaClienteOpcao[] = [];

  if (ehAdmin) {
    opcoes.push({
      texto: t($ => $.clientes.paginaCliente.web.alterarDados.titulo),
      to: '/comercial/produto/clientes/web/$codigo/alterar-dados',
    });
  }

  if (servicoWeb?.clienteId === clientIds.pontoWeb) {
    opcoes.push({
      texto: t($ => $.clientes.paginaCliente.web.empresasBanco),
      to: '/comercial/produto/clientes/web/$codigo/empresas-banco',
    });
  }

  if (!cancelado) {
    opcoes.push({
      texto: t($ => $.clientes.opcoes.cancelarContrato.titulo),
      to: '/comercial/produto/clientes/web/$codigo/cancelar-contrato',
    });
  }

  opcoes.push(
    {
      texto: t($ => $.clientes.opcoes.outrasSolicitacoes.titulo),
      to: '/comercial/produto/clientes/web/$codigo/outras-solicitacoes',
    },
    {
      texto: t($ => $.clientes.opcoes.minhasSolicitacoes.titulo),
      to: '/comercial/produto/clientes/web/$codigo/minhas-solicitacoes',
    },
  );

  if (textoBioWeb) {
    opcoes.push({
      texto: textoBioWeb,
      to: '/comercial/produto/clientes/web/$codigo/bioweb',
    });
  }

  return (
    <PaginaCliente
      search={search}
      carregando={isPending}
      erro={error}
      opcaoAberta={<Outlet />}
      opcoes={opcoes}
    >
      <ServicoWebPageComponent />
    </PaginaCliente>
  );
}
